
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Alert, Col, FormGroup, FormLabel, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { addRelation } from '@/Services/ProgrammationManagerService'
import { getItemsOptionsFromTaskList, getWorksOptionsFromTaskList } from './RelationsHelper'
import { updateRelationsAction } from '@/Redux/Actions/ProgrammationManagerActions'

import Modal from '@/Components/Modal/Modal'
import Button from '@/Components/Button/Button'
import InputSelect from '../../components/InputSelect'
import InputText from '../../components/InputText'

import AddICon from '../../../SvgImages/Add'

import './RelationsCreator.scss'

const RelationsCreator = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened, setEditingItem, patchItemOpened } = useProgrammation()

  const macroResult = useSelector((state: any) => state.MacroReducer.result)

  const [ showModal, setShowModal ] = useState(false)
  const [ taskA, setTaskA ] = useState('')
  const [ taskB, setTaskB ] = useState('')
  const [ showCurrentItemError, setShowCurrentItemError ] = useState(false)
  const [ showDuplicationError, setShowDuplicationError ] = useState(false)
  const [ relationType, setRelationType ] = useState('start_to_start')
  const [ creating, setCreating ] = useState(false)

  const relationsTypes = [
    {
      value: 'start_to_start',
      label: t('RELATIONS_CREATOR:Starts'),
    },
    {
      value: 'end_to_start',
      label: t('RELATIONS_CREATOR:Ends'),
    },
  ]

  const tasksOptions = useMemo(() => {

    if (itemOpened.type !== 'trabajo') {

      return getItemsOptionsFromTaskList({
        list: macroResult,
        currentItem: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentItem'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    } else if (itemOpened.type === 'trabajo') {

      return getWorksOptionsFromTaskList({
        list: macroResult,
        currentWork: itemOpened,
        currentLabel: t('RELATIONS_CREATOR:CurrentWork'),
        noNameText: t('RELATIONS_CREATOR:Nameless'),
        level: 0,
      })
    }
  }, [macroResult, itemOpened])

  const handleAddRelation = async () => {

    setCreating(true)
    setEditingItem(true)

    const [ error, data ] = await addRelation(relationType, taskA, taskB)

    setCreating(false)
    setEditingItem(false)

    if (error) {
      return console.log(error)
    }

    patchItemOpened({
      relations: [...itemOpened.relations, data.relation]
    })

    dispatch(updateRelationsAction(data, true))

    setShowModal(false)

    setTaskA('')
    setTaskB('')
    setRelationType('start_to_start')
  }

  const handleCancel = () => {

    setShowModal(false)
    setTaskA('')
    setTaskB('')
    setRelationType('start_to_start')
    setShowCurrentItemError(false)
  }

  useEffect(() => {

    if (!tasksOptions || !showModal) return

    console.log('tasksOptions', tasksOptions)

    setTaskA(itemOpened._id)
  }, [tasksOptions, showModal])

  useEffect(() => {

    const validateRelation = () => {

      const areBothTasksSelected = taskA && taskB

      const taskAIsCurrentItem = taskA === itemOpened._id
      const taskBIsCurrentItem = taskB === itemOpened._id

      const currentIsSelected = taskAIsCurrentItem || taskBIsCurrentItem

      if (!areBothTasksSelected) return

      if (taskA === taskB && currentIsSelected) {

        setShowDuplicationError(true)
        return
      }

      if (!taskAIsCurrentItem && !taskBIsCurrentItem) {

        console.log('no ha sido seleccionado', taskA, taskB, itemOpened._id)

        setShowCurrentItemError(true)
      }
    }

    setShowCurrentItemError(false)
    setShowDuplicationError(false)

    validateRelation()

    return () => {

      setShowCurrentItemError(false)
      setShowDuplicationError(false)
    }
  }, [taskA, taskB])

  return (
    <Fragment>
      <Button
        variant="primary"
        onClick={() => setShowModal(true)}
        className='mb-2'
      >
        <AddICon fill="#fff" height={24} width={24} />
      </Button>

      <Modal
        show={showModal}
        title={t('RELATIONS_CREATOR:Title')}
        onClose={handleCancel}
        backdropClassName='relations-creator-backdrop'
        className='relations-creator'
        showCloseButton={true}
        scrollable
        zIndex='2'
      >
        <FormGroup>
          <FormLabel>
            {t('RELATIONS_CREATOR:When')}
          </FormLabel>
          <Row>
            <Col sm={8}>
              <InputSelect
                placeholder={itemOpened.type === 'trabajo' ? t('RELATIONS_CREATOR:SelectAWork') : t('RELATIONS_CREATOR:SelectAnItem')}
                value={taskA}
                options={tasksOptions}
                onChange={setTaskA}
                renderOption={(option: any) => (
                  <>
                    {option.isGroup && (
                      <optgroup key={option.value} label={option.label}></optgroup>
                    )}
                    {!option.isGroup && (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    )}
                  </>
                )}
              />
            </Col>
            <Col sm={4}>
              <InputSelect
                value={relationType}
                options={relationsTypes}
                onChange={setRelationType}
                renderOption={(option: any) => (
                  <>
                    {option.isGroup && (
                      <optgroup key={option.value} label={option.label}></optgroup>
                    )}
                    {!option.isGroup && (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <FormLabel>
            {t('RELATIONS_CREATOR:Then')}
          </FormLabel>
          <Row>
            <Col sm={8}>
              <InputSelect
                placeholder={itemOpened.type === 'trabajo' ? t('RELATIONS_CREATOR:SelectAWork') : t('RELATIONS_CREATOR:SelectAnItem')}
                value={taskB}
                options={tasksOptions}
                onChange={setTaskB}
                renderOption={(option: any) => (
                  <>
                    {option.isGroup && (
                      <optgroup key={option.value} label={option.label}></optgroup>
                    )}
                  {!option.isGroup && (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    )}
                  </>
                )}
              />
            </Col>
            <Col sm={4}>
                <InputText
                  value="starts"
                  disabled
                />
            </Col>
          </Row>
        </FormGroup>
        {showCurrentItemError && (
          <Row>
            <Col>
              <Alert variant="danger">
                <strong>{itemOpened.name}</strong> must be selected
              </Alert>
            </Col>
          </Row>
        )}
        {showDuplicationError && (
          <Row>
            <Col>
              <Alert variant="danger">
                <strong>{itemOpened.name}</strong> cannot be selected twice
              </Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col style={{ textAlign: 'right' }}>
            <Button
              variant='primary'
              onClick={() => handleAddRelation()}
              disabled={!taskA || !taskB || showCurrentItemError || showDuplicationError}
            >
              <AddICon fill="#fff" height={24} width={24} />{' '}
              {!creating && t('RELATIONS_CREATOR:AddButton')}
              {creating && t('RELATIONS_CREATOR:AddButtonAdding')}
            </Button>
          </Col>
        </Row>
      </Modal>
    </Fragment>
  )
}

export default RelationsCreator
